import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import { Button } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { DbUserAclElevationWishlistInfos } from "../../../Core/Api/Types/db-user-acl-elevation-wishlist-infos";
import { OperationRequestStatus } from "../../../Core/Api/Types/operation-request-status";
import { UserHasAcl } from "../../../Utils/helpers";
import { ModalContext } from "../../Modals";
import ModalApprovalAction, {
  ModalApprovalActionElementProps,
} from "../../Modals/ModalApprovalAction/modal-approval-action";
import SelectModeBanner from "../SelectModeBanner";

export interface DashboardApprovalsBannerProps {
  handleCloseClick: React.MouseEventHandler;
  items: DbUserAclElevationWishlistInfos[];
}

const DashboardApprovalsBanner: React.FC<DashboardApprovalsBannerProps> = (props) => {
  const { t } = useTranslation();
  const { handleCloseClick, items } = props;
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const approve = () => {
    if (!openModal) return;
    const onClose: ModalApprovalActionElementProps["onClose"] = (data) => {};

    openModal<ModalApprovalActionElementProps>(ModalApprovalAction, {
      onClose,
      operation: "Approve",
      approvals: items.filter((x) => x.status === OperationRequestStatus.READY),
    });
  };

  const decline = () => {
    if (!openModal) return;
    const onClose: ModalApprovalActionElementProps["onClose"] = (data) => {};

    openModal<ModalApprovalActionElementProps>(ModalApprovalAction, {
      onClose,
      operation: "Decline",
      approvals: items.filter((x) => x.status === OperationRequestStatus.READY),
    });
  };

  const approveAndRun = () => {
    if (!openModal) return;
    const onClose: ModalApprovalActionElementProps["onClose"] = (data) => {};

    openModal<ModalApprovalActionElementProps>(ModalApprovalAction, {
      onClose,
      operation: "ApproveAndRun",
      approvals: items.filter((x) => x.status === OperationRequestStatus.READY),
    });
  };

  const run = () => {
    if (!openModal) return;
    const onClose: ModalApprovalActionElementProps["onClose"] = (data) => {};

    openModal<ModalApprovalActionElementProps>(ModalApprovalAction, {
      onClose,
      operation: "Run",
      approvals: items.filter((x) => x.status === OperationRequestStatus.APPROVED),
    });
  };

  const statusOfApprovals = React.useMemo(() => {
    if (!items || items.length === 0) return null;
    return items[0].status; // assuming all items have the same status
  }, [items]);

  return (
    <SelectModeBanner
      text={t("banner:dashboardApprovalBanner.body.itemApprovalSelected", { count: items.length })}
      onCloseClick={handleCloseClick}
    >
      {statusOfApprovals === OperationRequestStatus.READY && (
        <>
          {" "}
          <Button
            variant="whiteSoft"
            startIcon={<ThumbUpOutlinedIcon />}
            onClick={approve}
            disabled={!UserHasAcl(AclTypes.aclElevationWishlistUpdate)}
          >
            {t("button:approve")}
          </Button>
          <Button
            variant="whiteSoft"
            startIcon={<ThumbDownOutlinedIcon />}
            onClick={decline}
            disabled={!UserHasAcl(AclTypes.aclElevationWishlistUpdate)}
          >
            {t("common:decline")}
          </Button>
          <Button
            variant="white"
            startIcon={<PlayCircleOutlineOutlinedIcon />}
            onClick={approveAndRun}
            disabled={!UserHasAcl(AclTypes.aclElevationWishlistUpdate)}
          >
            {t("button:approveAndRunLowerCase")}
          </Button>
        </>
      )}

      {statusOfApprovals === OperationRequestStatus.APPROVED && (
        <Button
          variant="white"
          startIcon={<PlayCircleOutlineOutlinedIcon />}
          onClick={run}
          disabled={!UserHasAcl(AclTypes.aclElevationWishlistUpdate)}
        >
          {t("button:run")}
        </Button>
      )}
    </SelectModeBanner>
  );
};

export default DashboardApprovalsBanner;
